import { withErrorBoundary } from 'components/common/MartyErrorBoundary';
import { makePageLink } from 'helpers/SearchUtils';
import useMartyContext from 'hooks/useMartyContext';
import { cellsClasses, paginationClasses } from 'components/common/Pagination/Pagination.classes';
import { createPaginationItems } from 'components/common/Pagination/Pagination.utility';
import type { PaginationProps } from 'components/common/Pagination/Pagination.types';
import { PaginationCaret } from 'components/common/Pagination/PaginationCaret';
import { PaginationCell } from 'components/common/Pagination/PaginationCell';
import { PaginationEllipsis } from 'components/common/Pagination/PaginationEllipsis';

export const Pagination = ({ currentLocation, filters, firstPageIndex, onPagination, page, totalPages }: PaginationProps) => {
  const {
    testId,
    marketplace: { hasSeoTermPages }
  } = useMartyContext();

  const currentPage = page <= firstPageIndex ? firstPageIndex : page;
  const offset = 1 - firstPageIndex;
  const currentPageWithOffset = currentPage + offset;
  const totalPageCount = totalPages ?? 0;
  const items = createPaginationItems(currentPageWithOffset, totalPageCount);

  if (!items.length) {
    return null;
  }

  // Track pagination interaction if onPagination callback provided
  const onPaginationClick = (clickedPage: number | string) => {
    if (onPagination && clickedPage) {
      onPagination(clickedPage);
    }
    if (typeof window !== 'undefined') {
      window.scrollTo(0, 0);
    }
  };

  const isFirstPage = currentPageWithOffset <= 1;
  const isLastPage = currentPageWithOffset >= totalPageCount;

  return (
    <nav className={paginationClasses} aria-label="Pagination" data-test-id={testId('paginationV2')}>
      <PaginationCaret
        direction="prev"
        disabled={isFirstPage}
        href={isFirstPage ? undefined : makePageLink(filters, currentLocation ?? null, currentPage - 1, hasSeoTermPages) || ''}
        data-test-id={testId('prevButton')}
        onClick={isFirstPage ? undefined : () => onPaginationClick(`${currentPage}:prev`)}
      />
      <div className={cellsClasses}>
        {items.map((item, i) =>
          item.type === 'page' ? (
            <PaginationCell
              key={`page-${item.page}`}
              isActive={item.page === currentPageWithOffset}
              href={
                item.page === currentPageWithOffset
                  ? undefined
                  : makePageLink(filters, currentLocation ?? null, item.page - offset, hasSeoTermPages) || ''
              }
              title={`Page ${item.page}`}
              data-test-id={testId(item.page === currentPageWithOffset ? 'currentPageDesktop' : 'paginationNumber')}
              onClick={item.page === currentPageWithOffset ? undefined : () => onPaginationClick(item.page)}
            >
              {item.page}
            </PaginationCell>
          ) : (
            <PaginationEllipsis key={`ellipsis-${i}`} />
          )
        )}
      </div>
      <PaginationCaret
        direction="next"
        disabled={isLastPage}
        href={isLastPage ? undefined : makePageLink(filters, currentLocation ?? null, currentPage + 1, hasSeoTermPages) || ''}
        data-test-id={testId('nextButton')}
        onClick={isLastPage ? undefined : () => onPaginationClick(`${currentPage + 2}:next`)}
      />
    </nav>
  );
};

Pagination.displayName = 'Pagination';

export default withErrorBoundary('Pagination', Pagination);
